// admin_dashboard_controller.js
import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["countUp", "lastUpdated"];
  
  connect() {
    console.log("Admin Dashboard Controller Connected");
    
    // Initialize all features
    this.initializeCountUpAnimations();
    this.initializeDarkMode();
    this.updateLastUpdatedTime();
    this.checkAllCaughtUp();
    this.initializeKeyboardShortcuts();
    
    // Update time every minute
    this.timeUpdateInterval = setInterval(() => this.updateLastUpdatedTime(), 60000);
    
    console.log("✨ Modern Admin Dashboard loaded");
  }
  
  disconnect() {
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
    }
  }
  
  // ========== COUNT-UP ANIMATION ==========
  initializeCountUpAnimations() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.dataset.animated) {
          this.animateCountUp(entry.target);
          entry.target.dataset.animated = 'true';
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    
    this.countUpTargets.forEach(el => observer.observe(el));
  }
  
  animateCountUp(element) {
    const targetValue = element.getAttribute('data-count-target');
    const target = parseInt(targetValue);
    
    // Check if target is a valid number
    if (isNaN(target)) {
      console.error('Invalid count-target value for count-up:', targetValue, 'Element:', element);
      return;
    }
    
    const duration = 1000;
    const frameDuration = 1000 / 60;
    const totalFrames = Math.round(duration / frameDuration);
    let frame = 0;
    
    const counter = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      const currentCount = Math.round(target * this.easeOutCubic(progress));
      element.textContent = currentCount;
      
      if (frame === totalFrames) {
        clearInterval(counter);
        element.textContent = target;
      }
    }, frameDuration);
  }
  
  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }
  
  // ========== DARK MODE ==========
  initializeDarkMode() {
    const currentTheme = localStorage.getItem('dashboardTheme') || 'light';
    this.element.setAttribute('data-theme', currentTheme);
  }
  
  toggleTheme(event) {
    event.preventDefault();
    const currentTheme = this.element.getAttribute('data-theme');
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    
    this.element.setAttribute('data-theme', newTheme);
    localStorage.setItem('dashboardTheme', newTheme);
    
    this.createRipple(event.currentTarget, event);
  }
  
  // ========== RIPPLE EFFECT ==========
  createRipple(element, event) {
    const ripple = document.createElement('span');
    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;
    
    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = x + 'px';
    ripple.style.top = y + 'px';
    ripple.classList.add('ripple');
    
    element.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);
  }
  
  // ========== UPDATE TIMESTAMP ==========
  updateLastUpdatedTime() {
    if (!this.lastUpdatedTarget) return;
    
    const timestamp = parseInt(this.lastUpdatedTarget.getAttribute('data-time'));
    const now = Math.floor(Date.now() / 1000);
    const diff = now - timestamp;
    
    let text;
    if (diff < 60) {
      text = 'just now';
    } else if (diff < 3600) {
      const minutes = Math.floor(diff / 60);
      text = `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diff < 86400) {
      const hours = Math.floor(diff / 3600);
      text = `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diff / 86400);
      text = `${days} day${days > 1 ? 's' : ''} ago`;
    }
    
    this.lastUpdatedTarget.textContent = text;
  }
  
  // ========== CONFETTI CELEBRATION ==========
  checkAllCaughtUp() {
    const allCards = this.element.querySelectorAll('.metric-card');
    const successCards = this.element.querySelectorAll('.metric-card.card-success');
    
    if (allCards.length > 0 && allCards.length === successCards.length) {
      setTimeout(() => this.createConfetti(), 1500);
    }
  }
  
  createConfetti() {
    const colors = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];
    const confettiCount = 50;
    
    for (let i = 0; i < confettiCount; i++) {
      setTimeout(() => {
        const confetti = document.createElement('div');
        confetti.style.position = 'fixed';
        confetti.style.width = '10px';
        confetti.style.height = '10px';
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.left = Math.random() * window.innerWidth + 'px';
        confetti.style.top = '-20px';
        confetti.style.borderRadius = '50%';
        confetti.style.pointerEvents = 'none';
        confetti.style.zIndex = '9999';
        confetti.style.opacity = '1';
        confetti.style.transition = 'all 2s ease-out';
        
        document.body.appendChild(confetti);
        
        setTimeout(() => {
          confetti.style.top = window.innerHeight + 'px';
          confetti.style.left = (parseFloat(confetti.style.left) + (Math.random() - 0.5) * 200) + 'px';
          confetti.style.opacity = '0';
        }, 100);
        
        setTimeout(() => confetti.remove(), 2100);
      }, i * 30);
    }
  }
  
  // ========== KEYBOARD SHORTCUTS ==========
  initializeKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Ctrl/Cmd + K to toggle theme
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const themeButton = this.element.querySelector('#theme-toggle');
        if (themeButton) {
          themeButton.click();
        }
      }
    });
  }
  
  // ========== TABLE ROW ACTIONS ==========
  highlightRow(event) {
    const row = event.currentTarget;
    if (event.target.tagName !== 'A' && event.target.tagName !== 'BUTTON') {
      row.style.backgroundColor = 'var(--primary-50)';
      setTimeout(() => {
        row.style.backgroundColor = '';
      }, 300);
    }
  }
  
  // ========== PULSE WARNING CARDS ==========
  pulseWarningCards() {
    this.element.querySelectorAll('.metric-card.card-warning').forEach(card => {
      const count = parseInt(card.getAttribute('data-count'));
      if (count > 0) {
        const number = card.querySelector('.metric-number');
        if (number) {
          number.style.animation = 'pulse 2s ease-in-out infinite';
        }
      }
    });
  }
}

